import type { SupportedLanguage } from './translations/types';

// Build-environment accessors.
//
// These used to read `process.env.*`, which Vite only substitutes for keys it
// is explicitly told about in `define` — `process` itself is undefined in the
// browser, so every other lookup threw a ReferenceError at runtime. Vite
// exposes `VITE_`-prefixed variables on `import.meta.env`, so read them there
// and keep a guarded `process` fallback for any non-Vite consumer.
export const readEnv = (key: string): string | undefined => {
  const viteValue = (import.meta.env as Record<string, string | undefined>)?.[
    `VITE_${key.toUpperCase()}`
  ];
  if (viteValue) return viteValue;
  try {
    return (globalThis as { process?: { env?: Record<string, string> } })
      ?.process?.env?.[key];
  } catch {
    return undefined;
  }
};

/** Deploy branch for this build: 'main' | 'staging' | 'dev'. */
export const getMode = (): 'main' | 'staging' | 'dev' => {
  const branch = readEnv('link_type') || readEnv('branch') || '';
  if (branch.includes('main')) return 'main';
  if (branch.includes('staging')) return 'staging';
  return 'dev';
};

/**
 * Which ecomm_svc backend the lead/config calls hit. Mirrors 1.0's
 * `getApiMode`: the in-showroom (offline) build keeps its staging
 * metadata/assets but talks to the production backend, since a live showroom
 * has no separate backend environment.
 */
export const getApiMode = (): 'main' | 'staging' | 'dev' => {
  if (getShowroom() === 'in') return 'main';
  return getMode();
};

/**
 * Showroom build flag: 'in' for the in-showroom (offline) build, 'pre'
 * otherwise. Selects the WhatsApp template set on the backend and the
 * Salesforce source-code segment.
 */
export const getShowroom = (): string => readEnv('showroom') || 'pre';

export const getCountryParameter = () => {
  // Check in normal URL search parameters
  const searchParams = new URLSearchParams(window.location.search);
  const countryFromSearch = searchParams.get('country');
  if (countryFromSearch) {
    return countryFromSearch;
  }

  // Check in Hash URL search parameters
  const hash = window.location.hash;
  if (hash.includes('?')) {
    const hashParams = new URLSearchParams(hash.split('?')[1]);
    const countryFromHash = hashParams.get('country');
    if (countryFromHash) {
      return countryFromHash;
    }
  }

  return 'default';
};

/**
 * `?dealerName=` from the URL (search, then hash), '' when absent. Same lookup
 * as getCountryParameter; 1.0 reads the same param to sub-filter a country's
 * dealers.
 */
export const getDealerNameParameter = (): string => {
  const fromSearch = new URLSearchParams(window.location.search).get('dealerName');
  if (fromSearch) return fromSearch;

  const hash = window.location.hash;
  if (hash.includes('?')) {
    const fromHash = new URLSearchParams(hash.split('?')[1]).get('dealerName');
    if (fromHash) return fromHash;
  }

  return '';
};

// Countries where Matching Vehicles / dealer selection is offered. 'default'
// (no ?country=) is the UAE link.
const SUPPORTED_LANGUAGES: SupportedLanguage[] = ['en', 'fr', 'de', 'es', 'it', 'ar'];

const LOCAL_MATCHES_COUNTRIES = ['UAE', 'DEFAULT'];

// Models (uimetadata.config.model) with Matching Vehicles (header CTA +
// inventory lookup) and the post-build dealer-selection dialog switched on.
// Currently empty: the feature is off for every model (Tahoe was the only one
// on). Add a model here to turn it back on - its own
// `featureFlags.showLocalMatches` and the UAE-only rule below still apply.
const LOCAL_MATCHES_ENABLED_MODELS: string[] = [];

/**
 * Matching Vehicles button + the post-build dealer-selection dialog: on when the
 * model is in LOCAL_MATCHES_ENABLED_MODELS, the linkId's
 * `featureFlags.showLocalMatches` allows it (default true) AND the link is a UAE
 * one — dealer inventory only exists for the UAE today.
 */
export const isLocalMatchesEnabled = (uimetadata: any): boolean =>
  LOCAL_MATCHES_ENABLED_MODELS.includes(uimetadata?.config?.model) &&
  (uimetadata?.featureFlags?.showLocalMatches ?? true) &&
  LOCAL_MATCHES_COUNTRIES.includes(getCountryParameter().toUpperCase());

/**
 * Initial UI language, from a `lang` URL parameter (`?lang=ar`, or in the hash
 * for hash-routed links: `#/presentation?lang=ar`). Mirrors
 * getCountryParameter's search-then-hash lookup, since every deployed link is
 * hash-routed and the param lands after the `#`.
 *
 * Anything that isn't a language the app actually ships falls back to 'en'.
 */
export const getLanguageParameter = (): SupportedLanguage => {
  const searchParams = new URLSearchParams(window.location.search);
  const hash = window.location.hash;
  const hashParams = hash.includes('?')
    ? new URLSearchParams(hash.split('?')[1])
    : null;
  const raw = (searchParams.get('lang') || hashParams?.get('lang') || '')
    .trim()
    .toLowerCase();
  return SUPPORTED_LANGUAGES.includes(raw as SupportedLanguage)
    ? (raw as SupportedLanguage)
    : 'en';
};
